import React, { createContext, useContext, useEffect, useRef, useState } from 'react'
import { supabase } from '../lib/supabase'
import posthog, { isInternalTestEmail } from '../lib/posthog'

const AuthContext = createContext({})

export function AuthProvider({ children }) {
  const [user, setUser]       = useState(null)
  const [session, setSession] = useState(null)
  const [loading, setLoading] = useState(true)
  const identifiedUserId = useRef(null)

  function updateAuthenticatedUser(nextUser) {
    const nextUserId = nextUser?.id ?? null
    const previousUserId = identifiedUserId.current

    if (nextUserId && nextUserId !== previousUserId) {
      if (previousUserId) posthog.reset()
      // Internal test accounts (John's own jtav04*@gmail.com aliases) are
      // never identified at all — opt_out_capturing() instead guarantees
      // zero PostHog network requests, including the $identify call itself,
      // not just events after identify. This is independent of the
      // isLocalDev/isDevModeFlagged checks in posthog.js — neither
      // mechanism supersedes or depends on the other; each is separately
      // sufficient to stop capturing.
      if (isInternalTestEmail(nextUser.email)) {
        posthog.opt_out_capturing()
      } else {
        // Restore capturing if a prior identity in this same browser
        // session was an opted-out test account.
        if (posthog.has_opted_out_capturing()) posthog.opt_in_capturing()
        posthog.identify(nextUserId, { email: nextUser.email })
      }
    } else if (!nextUserId && previousUserId) {
      posthog.reset()
    }

    identifiedUserId.current = nextUserId
    setUser(prev => (prev?.id === nextUserId ? prev : nextUser))
  }

  useEffect(() => {
    // Identify an existing authenticated user on page refresh.
    supabase.auth.getSession().then(({ data: { session } }) => {
      setSession(session)
      updateAuthenticatedUser(session?.user ?? null)
      setLoading(false)
    })

    // Authentication state changes identify users once. TOKEN_REFRESHED keeps
    // the same ID and therefore does not issue another identify call.
    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => {
      setSession(session)
      updateAuthenticatedUser(session?.user ?? null)
    })

    return () => subscription.unsubscribe()
  }, [])

  const signUp = (email, password) =>
    supabase.auth.signUp({ email, password })

  const signIn = (email, password) =>
    supabase.auth.signInWithPassword({ email, password })

  const signInWithMagicLink = (email) =>
    supabase.auth.signInWithOtp({ email, options: { emailRedirectTo: window.location.origin } })

  // ── Email OTP (code-based) ────────────────────────────────────────────
  // Sends a 6-digit code (not a link — no emailRedirectTo) that's verified
  // in-page via verifyOtp. Used by OtpAuth.js, shared between the /auth
  // login page and ResultCTA's primary CTA flow.
  const sendOtp = (email) =>
    supabase.auth.signInWithOtp({ email })

  const verifyOtp = (email, token) =>
    supabase.auth.verifyOtp({ email, token, type: 'email' })

  const signOut = () => supabase.auth.signOut()

  // Shared post-sign-in redirect check for public tool pages with their own
  // embedded SignInModal (CalculatorPage.js, TriagePage.js — both public
  // routes via AuthAwareRoute, since /calculator and /spainsnapshot work
  // for anonymous and authenticated consumers alike). Both pages used to
  // just window.location.reload() on sign-in success — correct for a
  // consumer (isAuthenticated/mode are only computed once at mount, so a
  // full reload is the reliable way to re-enter as authenticated), but
  // wrong for a partner account: neither route checks user_type at all, so
  // a partner signing in through either page's embedded modal just
  // reloaded the calculator/triage form instead of landing on their
  // dashboard. All the correct partner-routing logic (HomeRedirect,
  // ProtectedRoute, PartnerRoute in App.js) never gets a chance to run
  // here, because nothing navigates through it. Became far more likely to
  // actually get hit once /calculator became portal home — an anonymous
  // advisor now lands there first by default. Pre-existing gap on both
  // pages independently, not caused by that change, just surfaced by it.
  //
  // Lives here rather than a standalone lib file — signOut just above is
  // the closest sibling to what this does (a hard-navigation action tied
  // to an auth-state change), and this context already owns every other
  // post-auth function; a third page adding its own embedded SignInModal
  // later just calls useAuth().redirectAfterSignIn() instead of
  // rediscovering this.
  const redirectAfterSignIn = async (fallback = () => window.location.reload()) => {
    const { data: { session: authSession } } = await supabase.auth.getSession()
    if (authSession) {
      const { data: profile } = await supabase
        .from('profiles')
        .select('user_type')
        .eq('id', authSession.user.id)
        .single()
      if (profile?.user_type === 'partner') {
        // Hard navigation, not react-router's navigate() — same reasoning
        // as every other post-auth transition in this codebase (see
        // ResultCTA.js's handleOtpSuccess/handleAdvisorOtpSuccess
        // comments): AuthContext/SubjectContext are mid-initialization off
        // a brand-new session, and a client-side navigate() would carry
        // whatever in-memory state they happen to be in at this exact
        // moment forward as-is. A full reload re-mounts everything fresh
        // against data that's already committed server-side.
        window.location.href = '/advisor'
        return
      }
    }
    fallback()
  }

  return (
    <AuthContext.Provider value={{ user, session, loading, signUp, signIn, signInWithMagicLink, sendOtp, verifyOtp, signOut, redirectAfterSignIn }}>
      {children}
    </AuthContext.Provider>
  )
}

export const useAuth = () => useContext(AuthContext)